import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { LINEAR_META, type LinearParams } from './linear.model';

/** Linear params with index signature for JsxParamStore compatibility */
type LinearParamsIndexed = LinearParams & { [key: string]: number };

export class LinearCreator extends BaseFunctionCreator<LinearParamsIndexed> {
  readonly functionType = 'linear';
  readonly defaultParams: LinearParamsIndexed = { ...LINEAR_META.defaultParams };
  readonly availableFeatures = LINEAR_META.availableFeatures;
  readonly handleConfigs = LINEAR_META.handleConfigs;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as LinearParamsIndexed;

    // Get stroke color for handles (needs to match curve color)
    const colorName = (display?.curve?.color ?? 'green') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      m: { value: initialParams.m, range: LINEAR_META.defaultRanges.m },
      b: { value: initialParams.b, range: LINEAR_META.defaultRanges.b },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const m = paramStore.getValue('m');
      const b = paramStore.getValue('b');
      return m * x + b;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return () => paramStore.getValue('m');
      }
      return () => 0;
    };

    return {
      id,
      type: 'linear',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as LinearParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
